import { Flex } from '@chakra-ui/react'
import { FileText, Voicemail } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '~/components/core'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { CustomTooltipActionButton } from '~/components/shared/CustomTooltip/CustomTooltipActionButton'
import { TranscriptInProgressToolTip } from '~/pages/Calls/CallDetails/TranscriptInProgressToolTip'
import { CallSentimentTooltip } from '~/pages/Calls/CallSentimentTooltip'
import { useTranscriptCellStyles } from '~/pages/Calls/componentStyles/CallsListItem.style'
import { Size } from '~/types/ThemeTypes'

import { VoicemailToolTip } from '../CallDetails/VoicemailToolTip'

interface TranscriptCellProps {
  columnStyles: Record<string, any>
  hasVoicemail?: boolean
  callId: number
  pbxId: number
  transcriptId?: number
  customerSentimentScore?: number
  onTranscriptClick?: () => void
  onVoicemailClick?: () => void
  transcriptionStatus?: string
  voicemailId?: number
}
export const TranscriptCell = ({
  columnStyles,
  hasVoicemail,
  callId,
  pbxId,
  transcriptId,
  transcriptionStatus,
  onTranscriptClick,
  onVoicemailClick,
  customerSentimentScore,
  voicemailId,
}: TranscriptCellProps) => {
  const { t } = useTranslation()
  const styles = useTranscriptCellStyles()

  const isTranscriptSentimentNegative =
    customerSentimentScore !== undefined && customerSentimentScore <= -1

  const showTranscriptIcon =
    transcriptionStatus === 'COMPLETED' ||
    transcriptionStatus === 'IN_PROGRESS' ||
    transcriptionStatus === 'AUDIO_TOO_LONG'

  let transcriptIconVariant: QuickActionButtonVariants =
    QuickActionButtonVariants.Default_100
  let isTranscriptIconDisabled = false
  let tooltipMessage = ''
  let transcriptAriaLabel = t(
    'calls.transcriptCell.viewTranscript',
    'View transcript',
  )
  let popoverContents: React.ReactNode = null

  if (transcriptionStatus === 'COMPLETED') {
    transcriptIconVariant = isTranscriptSentimentNegative
      ? QuickActionButtonVariants.Destructive_100
      : QuickActionButtonVariants.Default_100

    if (transcriptId) {
      popoverContents = (
        <CallSentimentTooltip
          pbxId={pbxId}
          callId={callId}
          callerSentiment={1}
          officeSentiment={-1}
          isSummaryTooltip={true}
        />
      )
    } else {
      transcriptIconVariant = QuickActionButtonVariants.Greyed_Out
      isTranscriptIconDisabled = true
      tooltipMessage = t(
        'calls.transcriptCell.noTranscriptAvailable',
        'No transcript available.',
      )
      transcriptAriaLabel = t(
        'calls.transcriptCell.noTranscript',
        'No transcript available',
      )
    }
  } else if (transcriptionStatus === 'IN_PROGRESS') {
    isTranscriptIconDisabled = true
    transcriptAriaLabel = t(
      'calls.transcriptCell.transcriptInProgress',
      'Transcript in progress',
    )
    transcriptIconVariant = QuickActionButtonVariants.Pulsate_Blue
    popoverContents = (
      <TranscriptInProgressToolTip
        content={
          <Text>
            {t(
              'calls.transcriptCell.inProgress',
              'Transcript is currently being generated, please check back later.',
            )}
          </Text>
        }
      />
    )
  } else if (transcriptionStatus === 'AUDIO_TOO_LONG') {
    isTranscriptIconDisabled = true
    transcriptAriaLabel = t(
      'calls.transcriptCell.transcriptUnavailable',
      'Transcript unavailable',
    )
    transcriptIconVariant = QuickActionButtonVariants.Greyed_Out
    popoverContents = (
      <TranscriptInProgressToolTip
        content={
          <Text>
            {t(
              'calls.transcriptCell.audioTooLong',
              'Transcript unavailable: the call audio was too long.',
            )}
          </Text>
        }
      />
    )
  }

  return (
    <Flex role="cell" css={{ ...columnStyles, ...styles.container }}>
      <Flex css={{ ...styles.wrapper }}>
        <Flex css={{ ...styles.iconWrapper }}>
          {showTranscriptIcon && (
            <CustomTooltipActionButton
              icon={FileText}
              variant={transcriptIconVariant}
              size={Size.sm}
              tooltipText={tooltipMessage}
              ariaLabel={transcriptAriaLabel}
              onButtonClick={onTranscriptClick}
              popoverContents={popoverContents}
              disabled={isTranscriptIconDisabled}
            />
          )}
          {hasVoicemail && (
            <CustomTooltipActionButton
              icon={Voicemail}
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              tooltipText=""
              ariaLabel={t(
                'calls.transcriptCell.openVoicemail',
                'Open voicemail',
              )}
              popoverContents={
                hasVoicemail && (
                  <VoicemailToolTip
                    voicemailId={voicemailId ?? 0}
                    pbxId={pbxId}
                    callId={callId}
                  />
                )
              }
              onButtonClick={onVoicemailClick}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}
